
import './complexRemuneration.less';
import * as _ from 'lodash';
import { certificationStates, remunerationOperations } from '../../objects/Payslip';
import { auditIncidents } from '../../objects/Audit';
import { GroupDialogResult } from '../groupDialog/GroupDialogResult';
import { getCertificationStateSubtitle, getRemunerationTitle } from '../../misc/textHelper';

/**
 * @typedef { import('./ComplexGroupModel').ComplexGroupModel } ComplexGroupModel
 * @typedef { import('../PaymentLock').PaymentLock } PaymentLock
 * @typedef { import('../../objects/Claim').Claim } Claim
 * @typedef { import('../../server/ApiServer').ApiServer } ApiServer
 */

class ComplexRemunerationController {
    static $inject = ['tromanServer', 'downloadService', 'dialogService'];
    /**
     * @param {ApiServer} tromanServer
     * @param {any} downloadService
     * @param {any} dialogService
     */
    constructor(tromanServer, downloadService, dialogService) {
        this._tromanServer = tromanServer;
        this._downloadService = downloadService;
        this._dialogService = dialogService;
        this.editRejection = false;

        this.title = '';
        this.defaultSubtitle = '';

        this.isCertifying = false;
        this.certificationMessage = '';
        this.isRejectingClaim = false;
        this.rejectClaimMessage = '';

        /** @type {ComplexGroupModel} */
        this.paymentGroup = null;
        /** @type {PaymentLock} */
        this.paymentLock = null;
    }

    $onChanges(changed) {
        if (changed.paymentGroup) {
            if (this.paymentGroup != null) {
                this.title = getRemunerationTitle(this.paymentGroup.remuneration.RemunerationType, this.paymentGroup.claim?.ClaimType);
                this.defaultSubtitle = getCertificationStateSubtitle(this.paymentGroup.remuneration.CertificationState);
                if (this.paymentGroup.isRejected) {
                    const rejections = _.filter(this.paymentGroup.remuneration.AuditEntries, x => x.Incident === auditIncidents.certificationRejected);
                    this.lastRejection = _.maxBy(rejections, x => x.Timestamp);
                    this.rejectClaimMessage = this.lastRejection.Message;
                }
            } else {
                this.title = '';
                this.defaultSubtitle = '';
                this.lastRejection = null;
                this.subClass = null;
            }
        }
    }

    get subClass() {
        switch (this.paymentGroup?.remuneration.CertificationState) {
            case certificationStates.recalled: return 'complex-remuneration-component--recalled';
            case certificationStates.rejected: return 'complex-remuneration-component--rejected';
            default: return '';
        }
    }

    get subtitle() {
        if (this.paymentGroup.operation === remunerationOperations.remove) {
            return 'raderas';
        } else if (this.paymentGroup.operation === remunerationOperations.certify) {
            return 'ny attest';
        } else if (this.paymentGroup.operation === remunerationOperations.rejectClaim) {
            return 'anspråk avslås';
        } else {
            return this.defaultSubtitle;
        }
    }

    remove() {
        this.paymentGroup.operation = remunerationOperations.remove;
        this.paymentGroup.payslipModel.paymentGroupChanged(this.paymentGroup);
    }

    certify() {
        if (this.paymentGroup.isRejected) {
            this.isCertifying = true;
        } else {
            this.paymentGroup.respondCertify(null);
        }
    }

    confirmCertification() {
        this.isCertifying = false;
        this.paymentGroup.respondCertify(this.certificationMessage);
    }
    
    cancelCertification() {
        this.isCertifying = false;
    }

    rejectClaim() {
        this.isRejectingClaim = true;
    }

    confirmRejectClaim(){
        this.isRejectingClaim = false;
        this.paymentGroup.respondRejectClaim(this.rejectClaimMessage);
    }

    cancelRejectClaim(){
        this.isRejectingClaim = false;
    }

    abort() {
        this.paymentGroup.operation = remunerationOperations.none;
        this.paymentGroup.certificationMessage = null;
        this.paymentGroup.payslipModel.paymentGroupChanged(this.paymentGroup);
    }

    get willRemovePayments() {
        return this.paymentGroup?.operation === remunerationOperations.remove || this.paymentGroup?.operation === remunerationOperations.rejectClaim;
    }

    get showAbort() {
        return this.paymentGroup?.operation !== remunerationOperations.none;
    }

    get showRemove() {
        if (this.showAbort) return false;
        if (this.isCertifying) return false;
        if (this.paymentGroup?.hasClaim) return false;
        return true;
    }

    get showCertify() {
        if (this.showAbort) return false;
        if (this.isCertifying) return false;
        if (!this.paymentGroup.hasActivePayments) return false;
        return true;
    }

    get showRejectClaim() {
        if (this.showAbort) return false;
        if (!this.paymentGroup.isRejected) return false;
        if (!this.paymentGroup?.hasClaim) return false;
        return true;
    }

    showDetails() {
        this._dialogService.showPaymentGroup(this.paymentGroup).then(result => {
            if (result instanceof GroupDialogResult) {
                result.apply(this.paymentGroup);
            }
        });
    }
}

angular.module("tromanWeb").component('ergoComplexRemuneration', {
    template: require('./complexRemuneration.html'),
    bindings: {
        paymentGroup: '<',
        paymentLock: '<',
        wageTypes: '<',
    },
    controller: ComplexRemunerationController,
});
